import React, { useState } from "react";
import { useNavigate, Link, useLocation } from "react-router-dom";
import { toast } from "sonner";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Loader2, Mail, Lock, ArrowLeft, Leaf } from "lucide-react";
import { useAuth } from "@/config/auth";
import { TOSS_LOGO_URL, BRAND } from "@/lib/branding";

export default function Login() {
  const navigate = useNavigate();
  const location = useLocation();
  const { login } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);

  const redirectTo = location.state?.from || "/advanced";

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!email || !password) { toast.error("Email and password required"); return; }
    setBusy(true);
    try {
      const u = await login(email.trim(), password);
      toast.success(`Welcome back, ${u.name}`);
      navigate(redirectTo, { replace: true });
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Invalid email or password");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-[#F4F8F4] via-[#F8F9F8] to-[#E9F2EA] flex items-center justify-center p-6" data-testid="login-page">
      <div className="w-full max-w-md">
        <button onClick={() => navigate("/")} className="flex items-center gap-1.5 text-xs text-[#617364] hover:text-[#43A047] mb-6" data-testid="back-home">
          <ArrowLeft className="w-3.5 h-3.5" /> Back to home
        </button>
        <Card className="border-[#E0E6E1] bg-white shadow-sm">
          <CardContent className="p-8">
            <div className="flex items-center gap-3 mb-6">
              <img src={TOSS_LOGO_URL} alt={BRAND.short} className="h-10 w-auto object-contain" />
              <div>
                <h1 className="text-xl font-medium text-[#1B291D]" style={{ fontFamily: 'Work Sans' }}>{BRAND.short}</h1>
                <p className="text-[10px] text-[#617364] tracking-wider uppercase">{BRAND.from} · Sign in</p>
              </div>
            </div>

            <h2 className="text-2xl font-light text-[#1B291D] mb-1" style={{ fontFamily: 'Work Sans' }}>Sign in</h2>
            <p className="text-xs text-[#617364] mb-6">Access your saved assessments, benchmarks &amp; analytics.</p>

            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Email</Label>
                <div className="relative mt-1">
                  <Mail className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#617364]" />
                  <Input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="you@company.com" className="pl-9 border-[#E0E6E1]" data-testid="login-email" autoFocus />
                </div>
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.15em] text-[#617364] font-bold">Password</Label>
                <div className="relative mt-1">
                  <Lock className="w-4 h-4 absolute left-3 top-1/2 -translate-y-1/2 text-[#617364]" />
                  <Input type="password" value={password} onChange={(e) => setPassword(e.target.value)} placeholder="••••••••" className="pl-9 border-[#E0E6E1]" data-testid="login-password" />
                </div>
              </div>

              <Button type="submit" disabled={busy} className="w-full bg-[#1B291D] hover:bg-[#2a3d2e] text-white" data-testid="login-submit">
                {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Signing in…</> : "Sign in"}
              </Button>
            </form>

            <div className="mt-6 pt-5 border-t border-[#E0E6E1] text-center">
              <p className="text-xs text-[#617364]">
                Don't have an account? <Link to="/register" className="text-[#43A047] font-medium hover:underline" data-testid="link-register">Create one</Link>
              </p>
            </div>

            <div className="mt-4 p-3 rounded-lg bg-[#43A047]/5 border border-[#43A047]/15 flex items-start gap-2">
              <Leaf className="w-4 h-4 text-[#43A047] flex-shrink-0 mt-0.5" />
              <p className="text-[11px] text-[#617364] leading-relaxed">
                <strong className="text-[#1B291D]">Just exploring?</strong> Try the no-login{" "}
                <Link to="/basic" className="text-[#43A047] underline">Basic Assessment</Link>{" "}— a 5-stage quick {BRAND.short} calculator.
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
}
